import React, { useEffect, useState, useCallback } from "react";
import { ArrowUpRight, ArrowDownRight, Minus, RefreshCw, AlertCircle, PlugZap } from "lucide-react";
import { marketingApi, fmtInt } from "./marketingApi";

const PALETTE = {
  navy: "#0A2A5E",
  ice: "#E6F2FF",
  iceBorder: "#CCE2F8",
  up: "#059669",
  down: "#DC2626",
  neutral: "#64748B",
};

function fmtDeltaPct(v) {
  if (v === null || v === undefined) return "—";
  const sign = v > 0 ? "+" : "";
  return `${sign}${v.toFixed(1)}%`;
}

function deltaColor(v) {
  if (v === null || v === undefined) return PALETTE.neutral;
  if (v > 0) return PALETTE.up;
  if (v < 0) return PALETTE.down;
  return PALETTE.neutral;
}

function DeltaIcon({ v }) {
  const s = 16;
  if (v === null || v === undefined || v === 0) return <Minus size={s} aria-hidden="true" />;
  if (v > 0) return <ArrowUpRight size={s} aria-hidden="true" />;
  return <ArrowDownRight size={s} aria-hidden="true" />;
}

function MetricCard({ label, source, metric, testId }) {
  const { current, previous, delta_pct, status } = metric || {};
  const isLoading = status === undefined;
  const isDisconnected = status === "disconnected";
  const isError = status && status.startsWith && status.startsWith("error");

  if (isLoading) {
    return (
      <div data-testid={`${testId}-loading`} className="rounded-xl border border-slate-200 bg-white p-5 animate-pulse">
        <div className="h-3 w-24 bg-slate-200 rounded" />
        <div className="h-9 w-32 bg-slate-200 rounded mt-3" />
        <div className="h-3 w-28 bg-slate-200 rounded mt-3" />
      </div>
    );
  }

  if (isDisconnected) {
    return (
      <div
        data-testid={`${testId}-disconnected`}
        role="region"
        aria-label={`${label} desconectado`}
        className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-5"
      >
        <div className="flex items-center gap-2 text-slate-500">
          <PlugZap size={16} aria-hidden="true" />
          <div className="text-xs uppercase tracking-wider font-semibold">{label}</div>
        </div>
        <div className="mt-2 text-sm text-slate-600">Integración no configurada</div>
        <div className="mt-1 text-[11px] text-slate-400">{source} · completa credenciales en Integraciones</div>
      </div>
    );
  }

  if (isError) {
    return (
      <div
        data-testid={`${testId}-error`}
        role="region"
        aria-label={`${label} con error`}
        className="rounded-xl border border-red-200 bg-red-50/50 p-5"
      >
        <div className="flex items-center gap-2 text-red-600">
          <AlertCircle size={16} aria-hidden="true" />
          <div className="text-xs uppercase tracking-wider font-semibold">{label}</div>
        </div>
        <div className="mt-2 text-sm text-red-700">Error temporal al consultar la API</div>
        <div className="mt-1 text-[11px] text-red-500">{source} · reintenta en unos minutos</div>
      </div>
    );
  }

  const color = deltaColor(delta_pct);

  return (
    <div
      data-testid={testId}
      role="region"
      aria-label={`${label}: ${fmtInt(current)} actuales, variación ${fmtDeltaPct(delta_pct)}`}
      className="rounded-xl border border-[#CCE2F8] bg-white p-5 shadow-sm hover:shadow-md transition-shadow focus-within:ring-2 focus-within:ring-[#0A2A5E] focus-within:ring-offset-1"
    >
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wider font-semibold text-[#0A2A5E]">{label}</div>
        <span
          className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold"
          style={{ backgroundColor: `${color}1A`, color }}
          data-testid={`${testId}-delta`}
          aria-label={`variación ${fmtDeltaPct(delta_pct)} vs 30 días anteriores`}
        >
          <DeltaIcon v={delta_pct} />
          {fmtDeltaPct(delta_pct)}
        </span>
      </div>
      <div className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 tabular-nums">
        {fmtInt(current)}
      </div>
      <div className="mt-2 text-[11px] text-slate-500">
        30 días anteriores: <span className="tabular-nums font-semibold text-slate-700">{fmtInt(previous)}</span>
      </div>
      <div className="mt-1 text-[11px] text-slate-400">{source}</div>
    </div>
  );
}

export default function Last30dWidget() {
  const [data, setData] = useState(null); // null = loading
  const [err, setErr] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const d = await marketingApi.kpiLast30d();
      setData(d);
      setErr(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Error de red");
      setData({}); // stop loading state
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const metrics = data?.metrics || {};
  const period = data?.period?.current;
  const updatedAt = data?.updated_at ? new Date(data.updated_at) : null;

  return (
    <section
      data-testid="last30d-widget"
      aria-labelledby="last30d-heading"
      className="rounded-2xl border border-[#CCE2F8] bg-gradient-to-br from-white via-white to-[#E6F2FF]/40 p-5 sm:p-6 shadow-sm"
    >
      <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div>
          <h2 id="last30d-heading" className="text-lg font-semibold tracking-tight text-[#0A2A5E]">
            Últimos 30 días
          </h2>
          <p className="text-xs text-slate-500 mt-0.5" data-testid="last30d-period">
            {period ? (
              <>Rango <span className="font-semibold text-slate-700">{period.start}</span> → <span className="font-semibold text-slate-700">{period.end}</span></>
            ) : (
              <>Comparado con los 30 días anteriores</>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {updatedAt && (
            <span
              className="text-[11px] text-slate-400 hidden sm:inline"
              data-testid="last30d-updated-at"
              title={updatedAt.toISOString()}
            >
              Actualizado {updatedAt.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })}
            </span>
          )}
          <button
            type="button"
            onClick={load}
            disabled={refreshing || data === null}
            data-testid="last30d-refresh"
            aria-label="Actualizar métricas"
            className="inline-flex items-center gap-1.5 rounded-lg border border-[#CCE2F8] bg-white px-3 py-1.5 text-xs font-medium text-[#0A2A5E] hover:bg-[#E6F2FF] disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-[#0A2A5E] focus:ring-offset-1"
          >
            <RefreshCw size={13} className={refreshing ? "animate-spin" : ""} aria-hidden="true" />
            Actualizar
          </button>
        </div>
      </header>

      {err && (
        <div
          role="alert"
          data-testid="last30d-error"
          className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
        >
          No pudimos consultar las métricas en tiempo real: {err}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <MetricCard
          testId="last30d-ga4"
          label="Sesiones web"
          source="GA4"
          metric={data === null ? undefined : metrics.ga4_sessions}
        />
        <MetricCard
          testId="last30d-gsc"
          label="Clics búsqueda"
          source="Google Search Console"
          metric={data === null ? undefined : metrics.gsc_clicks}
        />
      </div>

      <p className="mt-4 text-[11px] text-slate-400 leading-relaxed">
        Datos reales obtenidos directamente de GA4 y GSC. Nunca se inventan números: si una
        integración está desconectada o devuelve error se indica explícitamente. El asistente IA
        puede citar estas métricas y su período al responder.
      </p>
    </section>
  );
}
